import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useHistory } from "react-router";
import debounce from "lodash/debounce";
import makeQueryFromLocation from "theme/modules/Layer/makeQueryFromLocation";
import qs from "web/core/utils/queryString";

const getFacetValues = (layer, query) => {
  return layer.dynamicFacets.reduce(
    (values, attribute) => {
      const currentQueryValuesForAttribute = query.params.attributes.find(
        ({ name }) => name === attribute.code
      );
      const facetValue = currentQueryValuesForAttribute
        ? [].concat(currentQueryValuesForAttribute.value)
        : [];

      return {
        ...values,
        [attribute.code]: facetValue,
      };
    },
    {
      sort: JSON.stringify(query.params?.sort),
      pageLength: query.params?.size,
      price: {
        minPrice: query.minPrice,
        maxPrice: query.maxPrice,
      },
    }
  );
};

const useCurrentFacetValues = (layer) => {
  const location = useLocation();
  const history = useHistory();
  const query = useMemo(() => makeQueryFromLocation(location, {}), [location]);

  const valuesRef = useRef();
  const valuesFromQuery = useMemo(() => {
    const result = getFacetValues(layer, query);
    return result;
  }, [layer, query]);

  const [currentFacetValues, setCurrentFacetValues] = useState(valuesFromQuery);
  const [hasChangesForCodes, setChanges] = useState([]);

  useEffect(() => {
    // This allows to avoid unnecessary rerenders because of the dupplicated state
    // between the form and the url
    if (
      !valuesRef.current ||
      JSON.stringify(valuesRef.current) !== JSON.stringify(valuesFromQuery)
    ) {
      valuesRef.current = valuesFromQuery;
      setCurrentFacetValues(valuesFromQuery);
    }
  }, [valuesFromQuery]);

  const onFacetValueUpdate = useCallback((data) => {
    setCurrentFacetValues((values) => {
      return {
        ...values,
        ...data,
      };
    });
    setChanges((previousChanges) => {
      return previousChanges.concat(
        Object.keys(data).filter((key) => !previousChanges.includes(key))
      );
    });
  }, []);

  const applyFacetChanges = useCallback(() => {
    const { pathname, search } = location;
    const { price, sort, ...otherFacets } = currentFacetValues;
    const filters = {
      ...qs.parse(search),
      ...otherFacets,
      ...price,
      sort: sort && sort !== "" ? sort : undefined,
    };

    const updatedSearch = qs.stringify(filters);
    // This is needed so that no additional history.push are performed
    // when a modal route is rendered
    if (search.slice(1) !== updatedSearch) {
      history.push({
        pathname,
        search: updatedSearch,
      });
    }
    setChanges([]);
  }, [currentFacetValues, history, location]);

  return [
    currentFacetValues,
    onFacetValueUpdate,
    hasChangesForCodes,
    applyFacetChanges,
  ];
};

const useDebounce = (fn, delay) => {
  return useMemo(() => debounce(fn, delay), [fn, delay]);
};

const useLayoutFacets = (layer, onSubmit, applyOnChange) => {
  const [
    currentFacetValues,
    onFacetValueUpdate,
    hasChangesForCodes,
    applyFacetChanges,
  ] = useCurrentFacetValues(layer);

  useEffect(() => {
    if (applyOnChange && hasChangesForCodes.length > 0) {
      applyFacetChanges();
    }
  }, [applyFacetChanges, hasChangesForCodes, applyOnChange]);

  const onSubmitHandler = (data) => {
    onFacetValueUpdate(data);
    applyFacetChanges();
    onSubmit && onSubmit();
  };

  const onChangeHandler = useDebounce((data) => {
    onFacetValueUpdate(data);
  }, 50);

  const onReset =
    (name, resetValue = []) =>
    () => {
      onFacetValueUpdate({
        [name]: resetValue,
      });
    };

  const onResetAll = () => {
    onFacetValueUpdate(
      layer.activeFacets
        .filter((facet) => facet.code !== "price")
        .reduce(
          (value, facet) => {
            return {
              ...value,
              [facet.code]: [],
            };
          },
          {
            sort: currentFacetValues.sort,
            pageLength: currentFacetValues.pageLength,
            price: {
              minPrice: undefined,
              maxPrice: undefined,
            },
          }
        )
    );
  };

  return [
    currentFacetValues,
    hasChangesForCodes,
    onSubmitHandler,
    onChangeHandler,
    onReset,
    onResetAll,
  ];
};

export default useLayoutFacets;
